class ForzaProductRedesign extends HTMLElement {
  connectedCallback() {
    this.media = Array.from(this.querySelectorAll('[data-frp-media]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-frp-thumb]'));
    this.activeMedia = Math.max(0, this.media.findIndex((item) => item.classList.contains('is-active')));
    this.variants = this.readJson('[data-frp-variants]') || [];
    this.inventory = this.readJson('[data-frp-inventory]') || [];
    this.variantSelect = this.querySelector('[data-frp-variant-select]');
    this.form = this.querySelector('[data-frp-product-form]');
    this.addButton = this.querySelector('.frp__add');

    this.querySelector('[data-frp-prev]')?.addEventListener('click', () => this.showMedia(this.activeMedia - 1));
    this.querySelector('[data-frp-next]')?.addEventListener('click', () => this.showMedia(this.activeMedia + 1));

    this.stage = this.querySelector('.frp__stage');

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.showMedia(index, { play: thumb.hasAttribute('data-frp-autoplay') }));
    });

    this.variantSelect?.addEventListener('change', () => {
      const variant = this.variants.find((item) => String(item.id) === this.variantSelect.value);
      if (variant) this.selectVariant(variant);
    });

    this.querySelectorAll('[data-frp-submit]').forEach((button) => {
      button.addEventListener('click', () => this.submitProduct());
    });

    this.setupStickyBar();
    this.setupFomo();
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    window.clearTimeout(this.fomoTimer);
    window.clearTimeout(this.fomoHideTimer);
    window.clearTimeout(this.fomoExitTimer);
  }

  readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Forzarocket PDP data could not be parsed.', error);
      return null;
    }
  }

  showMedia(index, { play = false } = {}) {
    if (!this.media.length) return;

    const normalizedIndex = (index + this.media.length) % this.media.length;
    this.activeMedia = normalizedIndex;

    this.media.forEach((item, itemIndex) => {
      const active = itemIndex === normalizedIndex;
      item.classList.toggle('is-active', active);
      item.toggleAttribute('hidden', !active);

      if (!active) {
        item.querySelectorAll('video').forEach((video) => video.pause());
      }
    });

    const activeItem = this.media[normalizedIndex];
    const activeVideo = activeItem.querySelector('video');
    this.stage?.classList.toggle('is-video', Boolean(activeItem.querySelector('video, iframe')));
    if (play && activeVideo) activeVideo.play().catch(() => {});

    this.thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === normalizedIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.thumbs[normalizedIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  showMediaById(mediaId) {
    if (!mediaId) return;
    const index = this.media.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
    if (index >= 0) this.showMedia(index);
  }

  selectVariant(variant) {
    const variantInput = this.form?.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const quantitySelector = this.form?.querySelector('quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input[name="quantity"]');
    const quantityRule = variant.quantity_rule || {};

    if (quantitySelector) quantitySelector.dataset.variantId = variant.id;
    if (quantityInput) {
      quantityInput.min = quantityRule.min || 1;
      quantityInput.step = quantityRule.increment || 1;
      if (quantityRule.max == null) quantityInput.removeAttribute('max');
      else quantityInput.max = quantityRule.max;
      quantityInput.disabled = !variant.available;
      if (Number(quantityInput.value) < Number(quantityInput.min)) quantityInput.value = quantityInput.min;
    }

    this.updateText('[data-frp-price]', this.formatMoney(variant.price));
    this.updateText('[data-frp-sticky-price]', this.formatMoney(variant.price));

    const compare = this.querySelector('[data-frp-compare]');
    if (compare) {
      const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
      compare.hidden = !hasCompare;
      compare.textContent = hasCompare ? this.formatMoney(variant.compare_at_price) : '';
    }

    const availability = variant.available ? this.dataset.inStockText : this.dataset.soldOutText;
    this.updateText('[data-frp-availability]', availability);
    this.updateText('[data-frp-sticky-stock]', availability);

    if (this.fomo) {
      const inventory = this.inventory.find((item) => String(item.id) === String(variant.id));
      this.fomo.dataset.available = String(Boolean(variant.available));
      this.fomo.dataset.stockCount = inventory?.quantity == null ? '' : String(inventory.quantity);
      this.fomo.dataset.previousStock = inventory?.previousStock == null ? '' : String(inventory.previousStock);
      this.fomo.dataset.tracksInventory = String(
        inventory?.management === 'shopify' && inventory?.policy === 'deny'
      );
      if (!variant.available) this.dismissFomo();
    }

    const addText = variant.available ? this.dataset.addText : this.dataset.soldOutText;
    const addTextNode = this.addButton?.querySelector('.add-to-cart-text__content');
    if (addTextNode) addTextNode.textContent = addText;
    if (this.addButton) this.addButton.disabled = !variant.available;

    this.querySelectorAll('[data-frp-submit]').forEach((button) => {
      button.disabled = !variant.available;
      if (button.hasAttribute('data-frp-sync-label')) button.textContent = addText;
    });

    const mediaId = variant.featured_media?.id || variant.featured_media_id;
    this.showMediaById(mediaId);

    const mediaUrl = variant.featured_media?.preview_image?.src;
    const addComponent = this.addButton?.closest('add-to-cart-component');
    if (addComponent && mediaUrl) addComponent.dataset.productVariantMedia = mediaUrl;
    const fomoImage = this.fomo?.querySelector('.frp__fomo-image');
    if (fomoImage && mediaUrl) {
      fomoImage.removeAttribute('srcset');
      fomoImage.src = mediaUrl;
    }

    if (window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  updateText(selector, value) {
    this.querySelectorAll(selector).forEach((node) => {
      node.textContent = value;
    });
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(Number(cents) / 100);
    } catch (_error) {
      return `${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  submitProduct() {
    if (!this.form || this.addButton?.disabled) return;
    this.form.requestSubmit(this.addButton || undefined);
  }

  setupStickyBar() {
    const sticky = this.querySelector('[data-frp-sticky]');
    if (!sticky || !this.addButton || !('IntersectionObserver' in window)) return;

    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting && window.scrollY > entry.boundingClientRect.top;
        sticky.classList.toggle('is-visible', visible);
        this.classList.toggle('has-sticky-visible', visible);
      },
      { threshold: 0.05 }
    );
    this.stickyObserver.observe(this.addButton);
  }

  setupFomo() {
    this.fomo = this.querySelector('[data-frp-fomo]');
    if (!this.fomo) return;

    this.fomoKicker = this.fomo.querySelector('[data-frp-fomo-kicker]');
    this.fomoLead = this.fomo.querySelector('[data-frp-fomo-lead]');
    this.fomoHighlight = this.fomo.querySelector('[data-frp-fomo-highlight]');
    this.fomoTail = this.fomo.querySelector('[data-frp-fomo-tail]');
    this.fomoTime = this.fomo.querySelector('[data-frp-fomo-time]');
    this.fomoProductMedia = this.fomo.querySelector('[data-frp-fomo-product-media]');
    this.fomoChart = this.fomo.querySelector('[data-frp-fomo-chart]');
    this.fomoChartLine = this.fomo.querySelector('[data-frp-fomo-chart-line]');
    this.fomoChartLabel = this.fomo.querySelector('[data-frp-fomo-chart-label]');
    this.fomoLocations = (this.fomo.dataset.locations || '')
      .split(';')
      .map((location) => location.trim())
      .filter(Boolean);

    const min = Number.parseInt(this.fomo.dataset.viewersMin, 10) || 5;
    const configuredMax = Number.parseInt(this.fomo.dataset.viewersMax, 10) || 12;
    this.fomoMin = Math.min(min, configuredMax);
    this.fomoMax = Math.max(min, configuredMax);
    this.fomoSeed = this.getFomoSeed();
    this.fomoSequence = this.getFomoSequence();
    this.fomoStorageKey = `forza-proof-seen-v3-${this.fomo.dataset.productId || 'product'}`;
    this.fomoHistoryKey = 'forza-proof-history-v3';
    this.fomoHistory = this.getFomoHistory();

    this.fomo.querySelector('[data-frp-fomo-close]')?.addEventListener('click', (event) => {
      event.currentTarget.blur();
      this.dismissFomo();
    });

    if (this.fomo.dataset.available !== 'true' || this.wasFomoRecentlySeen()) return;

    const delay = window.Shopify?.designMode ? 900 : 8200 + Math.floor(this.fomoRandom(3) * 2800);
    this.fomoTimer = window.setTimeout(() => {
      this.showFomo();
    }, delay);
  }

  getFomoSeed() {
    const key = `forza-proof-seed-v3-${this.fomo.dataset.productId || 'product'}`;

    try {
      const stored = window.sessionStorage.getItem(key);
      if (stored) return Number(stored);

      const seed = Math.floor(Math.random() * 2147483646) + 1;
      window.sessionStorage.setItem(key, String(seed));
      return seed;
    } catch (_error) {
      return Math.floor(Math.random() * 2147483646) + 1;
    }
  }

  getFomoSequence() {
    const key = `forza-proof-sequence-v3-${this.fomo.dataset.productId || 'product'}`;

    try {
      const sequence = (Number(window.sessionStorage.getItem(key)) || 0) + 1;
      window.sessionStorage.setItem(key, String(sequence));
      return sequence;
    } catch (_error) {
      return Math.floor(Math.random() * 1000) + 1;
    }
  }

  fomoRandom(offset = 0) {
    let value = (this.fomoSeed + (offset + this.fomoSequence * 11) * 2654435761) >>> 0;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    return (value >>> 0) / 4294967296;
  }

  getFomoHistory() {
    try {
      const history = JSON.parse(window.sessionStorage.getItem(this.fomoHistoryKey) || '{}');
      return history && typeof history === 'object' ? history : {};
    } catch (_error) {
      return {};
    }
  }

  rememberFomoValue(group, value, limit = 5) {
    const history = Array.isArray(this.fomoHistory[group]) ? this.fomoHistory[group] : [];
    this.fomoHistory[group] = [...history, value].slice(-limit);

    try {
      window.sessionStorage.setItem(this.fomoHistoryKey, JSON.stringify(this.fomoHistory));
    } catch (_error) {
      // History is an enhancement; private browsing must not block the notification.
    }
  }

  pickFomoValue(values, group, offset, historyLimit = 5) {
    const recent = Array.isArray(this.fomoHistory[group]) ? this.fomoHistory[group].slice(-historyLimit) : [];
    const unused = values.filter((value) => !recent.includes(value));
    const pool = unused.length > 0 ? unused : values;
    const value = pool[Math.floor(this.fomoRandom(offset) * pool.length)];
    this.rememberFomoValue(group, value, historyLimit);
    return value;
  }

  wasFomoRecentlySeen() {
    if (window.Shopify?.designMode) return false;

    try {
      const seenAt = Number(window.sessionStorage.getItem(this.fomoStorageKey));
      return seenAt > 0 && Date.now() - seenAt < 30 * 60 * 1000;
    } catch (_error) {
      return false;
    }
  }

  markFomoSeen() {
    try {
      window.sessionStorage.setItem(this.fomoStorageKey, String(Date.now()));
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes; the notification still works.
    }
  }

  selectFomoType() {
    const stock = Number.parseInt(this.fomo.dataset.stockCount, 10);
    const threshold = Number.parseInt(this.fomo.dataset.stockThreshold, 10) || 6;
    const hasLowStock = this.fomo.dataset.tracksInventory === 'true' && stock > 0 && stock <= threshold;
    const weeklyViews = Number.parseInt(this.fomo.dataset.weeklyViews, 10) || 0;
    const previousViews = Number.parseInt(this.fomo.dataset.previousWeekViews, 10) || 0;
    const interestType = previousViews > 0 && weeklyViews > previousViews ? 'trend' : weeklyViews > 0 ? 'views' : null;

    const mode = this.fomo.dataset.mode || 'balanced';
    let candidates = [];

    if (mode === 'orders') {
      if (this.fomoLocations.length > 0) candidates.push('order', 'order', 'order');
      if (hasLowStock) candidates.push('stock');
    } else if (mode === 'interest') {
      candidates.push('view', 'view');
      if (interestType) candidates.push(interestType, interestType);
      if (hasLowStock) candidates.push('stock');
    } else {
      if (this.fomoLocations.length > 0) candidates.push('order', 'order');
      candidates.push('view', 'view');
      if (interestType) candidates.push(interestType, interestType);
      if (hasLowStock) candidates.push('stock', 'stock');
    }

    if (candidates.length === 0) candidates = ['view'];
    const recentTypes = Array.isArray(this.fomoHistory.types) ? this.fomoHistory.types.slice(-1) : [];
    const fresh = candidates.filter((type) => !recentTypes.includes(type));
    const pool = fresh.length > 0 ? fresh : candidates;
    const type = pool[Math.floor(this.fomoRandom(4) * pool.length)];
    this.rememberFomoValue('types', type, 2);
    return type;
  }

  renderFomoEvent(type) {
    if (!this.fomoHighlight) return;

    this.setFomoChart();

    if (type === 'stock') {
      const stock = Number.parseInt(this.fomo.dataset.stockCount, 10);
      const previousStock = Number.parseInt(this.fomo.dataset.previousStock, 10) || 0;
      this.fomoKicker.textContent = 'Low stock';
      this.fomoLead.textContent = '';
      this.fomoHighlight.textContent = `Only ${stock} left`;
      this.fomoTail.textContent = ' in this color and ready to ship.';
      if (previousStock > stock) {
        const drop = Math.max(1, Math.round(((previousStock - stock) / previousStock) * 100));
        this.fomoTime.textContent = `Stock down ${drop}% this week`;
        this.setFomoChart('down', 'STOCK');
      } else {
        this.fomoTime.textContent = 'Shopify inventory';
      }
    } else if (type === 'order') {
      const location = this.pickFomoValue(
        this.fomoLocations,
        'locations',
        5,
        Math.max(1, Math.min(5, this.fomoLocations.length - 1))
      );
      const minuteOptions = Array.from({ length: 48 }, (_item, index) => index + 3);
      const minutes = this.pickFomoValue(minuteOptions, 'minutes', 6, 8);
      const copyIndex = this.pickFomoValue([0, 1, 2], 'orderCopy', 7, 2);
      const orderCopy = [
        { kicker: 'Recent order', lead: 'A collector in ', tail: ' recently ordered this binder.' },
        { kicker: 'Collector activity', lead: 'Someone in ', tail: ' just picked up this binder.' },
        { kicker: 'Another one ordered', lead: 'This binder is heading to ', tail: '.' },
      ][copyIndex];
      this.fomoKicker.textContent = orderCopy.kicker;
      this.fomoLead.textContent = orderCopy.lead;
      this.fomoHighlight.textContent = location;
      this.fomoTail.textContent = orderCopy.tail;
      this.fomoTime.textContent = `${minutes} min ago`;
    } else if (type === 'trend' || type === 'views') {
      const weeklyViews = Number.parseInt(this.fomo.dataset.weeklyViews, 10) || 0;
      const previousViews = Number.parseInt(this.fomo.dataset.previousWeekViews, 10) || 0;
      this.fomoKicker.textContent = type === 'trend' ? 'Interest is rising' : 'Popular this week';
      this.fomoLead.textContent = '';
      this.fomoHighlight.textContent = `${weeklyViews.toLocaleString()} product views`;
      this.fomoTail.textContent = ' in the last 7 days.';
      if (type === 'trend') {
        const growth = Math.max(1, Math.round(((weeklyViews - previousViews) / previousViews) * 100));
        this.fomoTime.textContent = `Interest up ${growth}% vs last week`;
        this.setFomoChart('up', '7D');
      } else {
        this.fomoTime.textContent = 'Measured over the last 7 days';
        this.setFomoChart('neutral', '7D');
      }
    } else {
      const range = this.fomoMax - this.fomoMin + 1;
      const counts = Array.from({ length: range }, (_item, index) => this.fomoMin + index);
      const count = this.pickFomoValue(counts, 'viewerCounts', 8, Math.min(4, counts.length - 1));
      const copyIndex = this.pickFomoValue([0, 1], 'viewCopy', 9, 1);
      this.fomoKicker.textContent = copyIndex === 0 ? 'Popular right now' : 'Live product interest';
      this.fomoLead.textContent = '';
      this.fomoHighlight.textContent = `${count} collectors`;
      this.fomoTail.textContent = copyIndex === 0 ? ' are viewing this binder right now.' : ' have this binder open now.';
      this.fomoTime.textContent = copyIndex === 0 ? 'Updated moments ago' : 'Live session activity';
    }
  }

  setFomoChart(direction, label = '7D') {
    const showChart = Boolean(direction && this.fomoChart && this.fomoChartLine);
    this.fomoProductMedia?.toggleAttribute('hidden', showChart);
    this.fomoChart?.toggleAttribute('hidden', !showChart);
    if (!showChart) return;

    const points = Array.from({ length: 7 }, (_item, index) => {
      const progress = index / 6;
      const base = direction === 'up' ? 43 - progress * 31 : direction === 'down' ? 11 + progress * 32 : 31 - progress * 8;
      const jitter = (this.fomoRandom(20 + index) - .5) * (direction === 'neutral' ? 12 : 7);
      return `${3 + index * 10.3},${Math.max(6, Math.min(46, base + jitter)).toFixed(1)}`;
    });

    this.fomoChart.classList.toggle('is-down', direction === 'down');
    this.fomoChartLine.setAttribute('points', points.join(' '));
    if (this.fomoChartLabel) this.fomoChartLabel.textContent = label;
  }

  showFomo() {
    if (document.hidden) {
      this.fomoTimer = window.setTimeout(() => this.showFomo(), 1500);
      return;
    }
    if (this.fomo.dataset.available !== 'true') return;

    this.renderFomoEvent(this.selectFomoType());
    this.markFomoSeen();
    this.fomo.setAttribute('aria-hidden', 'false');
    this.fomo.classList.add('is-visible');
    this.fomoHideTimer = window.setTimeout(() => this.dismissFomo(), 6800);
  }

  dismissFomo() {
    window.clearTimeout(this.fomoHideTimer);
    if (!this.fomo?.classList.contains('is-visible')) return;

    this.fomo.classList.add('is-leaving');
    this.fomoExitTimer = window.setTimeout(() => {
      this.fomo.classList.remove('is-visible', 'is-leaving');
      this.fomo.setAttribute('aria-hidden', 'true');
    }, 360);
  }
}

if (!customElements.get('forza-product-redesign')) {
  customElements.define('forza-product-redesign', ForzaProductRedesign);
}
